/**
 * Developed by Spell Solutions for Trivinci Systems, LLC.
 *
 * Developer attribution only. Ownership, licensing, and permitted use are
 * governed by the applicable agreement and applicable law.
 */
import { SESSIONS_API, SESSIONS_API_KEY } from "./config";

/**
 * Lightweight session-list cache. Used to resolve driver_name for any logger
 * across the app without refetching. Single in-flight fetch shared by callers.
 */

interface Session {
  logger_id?: string;
  driver_name?: string;
  status?: string;
  aws_region?: string;
  /** Client logo overlays, as PNG data URIs. Only ever present on the
   *  single-logger response — the list strips them. */
  logos?: string[];
  /** How much bigger than the base size to draw this car's logos. Set per car
   *  from the console; absent on rows saved before the control existed. */
  logo_scale?: number;
}

/** Overlay size multiplier applied when a row does not carry its own.
 *
 *  2, not 1: the original size was judged too small in use, so the baseline
 *  moved. Every car therefore renders at the larger size without needing an
 *  edit, and the console can still take an individual car back down to 1.
 *  Kept in step with LOGO_SCALE_DEFAULT in LoggerAdminLambda. */
export const LOGO_SCALE_DEFAULT = 2;
const LOGO_SCALE_MIN = 1;
const LOGO_SCALE_MAX = 3;

export interface LogoOverlay {
  logos: string[];
  /** Always a usable number — never NaN, never out of range. */
  scale: number;
}

let cache: Map<string, Session> | null = null;
let inflight: Promise<Map<string, Session>> | null = null;
let lastFetchedAt = 0;
const TTL_MS = 60_000;

async function fetchSessions(): Promise<Map<string, Session>> {
  // /sessions requires x-api-key — see SESSIONS_API_KEY comment in config.ts.
  // Without it, API Gateway returns 403 with no CORS headers, which the
  // browser surfaces as a misleading "No 'Access-Control-Allow-Origin'" error.
  const res = await fetch(SESSIONS_API, {
    mode: "cors",
    headers: { "x-api-key": SESSIONS_API_KEY },
  });
  if (!res.ok) throw new Error(`sessions ${res.status}`);
  const data = (await res.json()) as
    | Session[]
    | { sessions?: Session[]; items?: Session[] };
  // GetSessionsLambda answers `{items:[…]}`. The `sessions` key was never
  // emitted by it, so before `items` was handled here this map came back empty
  // and every driver_name lookup silently resolved to null.
  const list: Session[] = Array.isArray(data)
    ? data
    : Array.isArray((data as any)?.items)
    ? (data as any).items
    : Array.isArray(data?.sessions)
    ? data.sessions!
    : [];
  const map = new Map<string, Session>();
  for (const s of list) {
    if (s && s.logger_id) map.set(String(s.logger_id), s);
  }
  return map;
}

export async function getSessions(force = false): Promise<Map<string, Session>> {
  const fresh = Date.now() - lastFetchedAt < TTL_MS;
  if (!force && cache && fresh) return cache;
  if (inflight) return inflight;
  inflight = fetchSessions()
    .then((m) => {
      cache = m;
      lastFetchedAt = Date.now();
      return m;
    })
    .finally(() => {
      inflight = null;
    });
  return inflight;
}

export async function getDriverName(loggerId: string): Promise<string | null> {
  try {
    const m = await getSessions();
    return m.get(loggerId)?.driver_name ?? null;
  } catch {
    return null;
  }
}

/** Client logo overlays for ONE car.
 *
 *  Uses the single-logger endpoint (`?loggerId=`) because the fleet listing
 *  deliberately strips the blobs. Cached per logger for the lifetime of the
 *  page: logos change from the console, not mid-session, and re-fetching would
 *  pull tens of KB for no reason. Any failure resolves to an empty list so a
 *  logo problem can never stop the video from playing. */
const logoCache = new Map<string, Promise<LogoOverlay>>();

/** Clamp a stored scale into the supported range.
 *
 *  A bad value here would resize the overlay over live video, so anything that
 *  is not a finite number in range falls back to the default rather than being
 *  passed through to CSS. */
function safeScale(v: unknown): number {
  const n = typeof v === "number" ? v : Number(v);
  if (!Number.isFinite(n)) return LOGO_SCALE_DEFAULT;
  return Math.min(LOGO_SCALE_MAX, Math.max(LOGO_SCALE_MIN, n));
}

export function getLogos(loggerId: string): Promise<LogoOverlay> {
  const hit = logoCache.get(loggerId);
  if (hit) return hit;
  const p = (async () => {
    const empty: LogoOverlay = { logos: [], scale: LOGO_SCALE_DEFAULT };
    try {
      const url = `${SESSIONS_API}?loggerId=${encodeURIComponent(loggerId)}`;
      const res = await fetch(url, {
        mode: "cors",
        headers: { "x-api-key": SESSIONS_API_KEY },
      });
      if (!res.ok) return empty;
      const data = (await res.json()) as { items?: Session[] };
      const row = data?.items?.[0];
      const logos = row?.logos;
      if (!Array.isArray(logos)) return empty;
      return {
        // Only render what is unmistakably a PNG data URI.
        logos: logos.filter(
          (u) => typeof u === "string" && u.startsWith("data:image/png;base64,"),
        ),
        scale:
          row?.logo_scale === undefined || row?.logo_scale === null
            ? LOGO_SCALE_DEFAULT
            : safeScale(row.logo_scale),
      };
    } catch {
      return empty;
    }
  })();
  logoCache.set(loggerId, p);
  return p;
}

export async function getSession(loggerId: string): Promise<Session | null> {
  try {
    const m = await getSessions();
    return m.get(loggerId) ?? null;
  } catch {
    return null;
  }
}
